/**
 * NotificationsAnalyticsPage
 *
 * Mismo nivel que WhatsAppAnalyticsPage pero para push notifications.
 *
 *   - 5 KPI cards (enviados / tasa entrega / tasa apertura / tasa click / fallidos)
 *   - Line chart de engagement por día
 *   - Tabla de campañas recientes con métricas
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Send,
    CheckCheck,
    Eye,
    MousePointerClick,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    ArrowLeft,
    Bell,
    Loader2,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
} from "recharts";
import { Button } from "../../../shared/components/ui/Button";
import { notificationsApi } from "../api";
import type { NotificationCampaign } from "../types";

const MOCK_BRAND_ID = "brand-demo";

interface CampaignRow {
    id: string;
    title: string;
    segmentName: string;
    sentAt: string;
    recipients: number;
    delivered: number;
    opened: number;
    clicked: number;
}

export function NotificationsAnalyticsPage() {
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
    const [timeSeries, setTimeSeries] = useState<
        { day: string; entregado: number; abierto: number; clickeado: number }[]
    >([]);
    const [emptyState, setEmptyState] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await notificationsApi.analytics(MOCK_BRAND_ID, 30);
            if (res.success && res.data?.totals?.campaignsCount > 0) {
                const recent: CampaignRow[] = (res.data.recentCampaigns || []).map(
                    (c: NotificationCampaign) => ({
                        id: c.id,
                        title: c.title || "Notificación",
                        segmentName: c.segmentName || "—",
                        sentAt: c.sentAt || c.createdAt,
                        recipients: c.recipientCount || 0,
                        delivered: c.metrics?.delivered || 0,
                        opened: c.metrics?.opened || 0,
                        clicked: c.metrics?.clicked || 0,
                    }),
                );
                setCampaigns(recent);
                setTimeSeries(res.data.perDay);
                setEmptyState(false);
            } else {
                setCampaigns([]);
                setTimeSeries([]);
                setEmptyState(true);
            }
            setLoading(false);
        })();
    }, []);

    const totals = useMemo(() => {
        const sent = campaigns.reduce((a, c) => a + c.recipients, 0);
        const delivered = campaigns.reduce((a, c) => a + c.delivered, 0);
        const opened = campaigns.reduce((a, c) => a + c.opened, 0);
        const clicked = campaigns.reduce((a, c) => a + c.clicked, 0);
        const failed = campaigns.reduce(
            (a, c) => a + Math.max(0, c.recipients - c.delivered),
            0,
        );
        return {
            sent,
            delivered,
            opened,
            clicked,
            failed,
            deliveryRate: sent ? (delivered / sent) * 100 : 0,
            openRate: delivered ? (opened / delivered) * 100 : 0,
            clickRate: opened ? (clicked / opened) * 100 : 0,
        };
    }, [campaigns]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-500">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Cargando métricas…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-7xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/notifications"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a notificaciones
                </Link>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                                Métricas de notificaciones
                            </h1>
                            <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">
                                Últimos 30 días
                            </span>
                        </div>
                        <p className="text-slate-500 text-lg">
                            Performance de las notificaciones push enviadas: entrega,
                            apertura y clics.
                        </p>
                    </div>
                    <Link to="/notifications">
                        <Button variant="primary">
                            <Bell size={16} />
                            Nueva notificación
                        </Button>
                    </Link>
                </div>

                {emptyState ? (
                    <EmptyState />
                ) : (
                    <>
                        {/* KPI Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <KPICard
                                label="Notificaciones enviadas"
                                value={totals.sent.toLocaleString("es-AR")}
                                change={`${totals.failed.toLocaleString("es-AR")} fallidas`}
                                up={false}
                                icon={Send}
                            />
                            <KPICard
                                label="Tasa de entrega"
                                value={`${totals.deliveryRate.toFixed(1)}%`}
                                change={`${totals.delivered.toLocaleString("es-AR")} entregadas`}
                                up
                                icon={CheckCheck}
                                highlight="emerald"
                            />
                            <KPICard
                                label="Tasa de apertura"
                                value={`${totals.openRate.toFixed(1)}%`}
                                change={`${totals.opened.toLocaleString("es-AR")} abiertas`}
                                up
                                icon={Eye}
                                highlight="blue"
                            />
                            <KPICard
                                label="Tasa de clics"
                                value={`${totals.clickRate.toFixed(1)}%`}
                                change={`${totals.clicked.toLocaleString("es-AR")} clics`}
                                up
                                icon={MousePointerClick}
                                highlight="violet"
                            />
                            <KPICard
                                label="Campañas totales"
                                value={campaigns.length.toLocaleString("es-AR")}
                                change="enviadas"
                                up
                                icon={Bell}
                                highlight="slate"
                            />
                        </div>

                        {/* Chart */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <h3 className="font-semibold text-slate-900">
                                        Engagement por día
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        % entregado / abierto / clickeado por día
                                    </p>
                                </div>
                            </div>
                            <div className="h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={timeSeries}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#e2e8f0"
                                        />
                                        <XAxis
                                            dataKey="day"
                                            stroke="#94a3b8"
                                            fontSize={12}
                                        />
                                        <YAxis stroke="#94a3b8" fontSize={12} />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: 12,
                                                border: "1px solid #e2e8f0",
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: 11,
                                                paddingTop: 8,
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="entregado"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="abierto"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="clickeado"
                                            stroke="#8b5cf6"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Recent campaigns */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-slate-900">
                                        Notificaciones enviadas
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {campaigns.length}{" "}
                                        {campaigns.length === 1 ? "campaña" : "campañas"}{" "}
                                        en los últimos 30 días
                                    </p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                        <tr>
                                            <th className="text-left px-6 py-3 font-medium">
                                                Notificación
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium">
                                                Segmento
                                            </th>
                                            <th className="text-right px-4 py-3 font-medium">
                                                Enviadas
                                            </th>
                                            <th className="text-right px-4 py-3 font-medium">
                                                Entrega
                                            </th>
                                            <th className="text-right px-4 py-3 font-medium">
                                                Apertura
                                            </th>
                                            <th className="text-right px-4 py-3 font-medium">
                                                Click
                                            </th>
                                            <th className="text-right px-6 py-3 font-medium">
                                                Fecha
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {campaigns.map((c) => {
                                            const dRate =
                                                c.recipients > 0
                                                    ? (c.delivered / c.recipients) * 100
                                                    : 0;
                                            const oRate =
                                                c.delivered > 0
                                                    ? (c.opened / c.delivered) * 100
                                                    : 0;
                                            const clRate =
                                                c.opened > 0
                                                    ? (c.clicked / c.opened) * 100
                                                    : 0;
                                            return (
                                                <tr
                                                    key={c.id}
                                                    className="hover:bg-slate-50/60 transition-colors"
                                                >
                                                    <td className="px-6 py-3.5">
                                                        <p className="font-medium text-slate-900 truncate max-w-xs">
                                                            {c.title}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <span className="text-xs text-slate-500">
                                                            {c.segmentName}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3.5 text-right font-semibold text-slate-900">
                                                        {c.recipients.toLocaleString("es-AR")}
                                                    </td>
                                                    <td className="px-4 py-3.5 text-right">
                                                        <span className="font-medium text-emerald-600">
                                                            {dRate.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3.5 text-right">
                                                        <span className="font-medium text-blue-600">
                                                            {oRate.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3.5 text-right">
                                                        <span className="font-medium text-violet-600">
                                                            {clRate.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-3.5 text-right text-xs text-slate-500">
                                                        {new Date(
                                                            c.sentAt,
                                                        ).toLocaleDateString("es-AR", {
                                                            day: "numeric",
                                                            month: "short",
                                                        })}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ─── Sub-components ─── */

interface KPICardProps {
    label: string;
    value: string;
    change: string;
    up: boolean;
    icon: typeof Send;
    highlight?: "emerald" | "blue" | "violet" | "slate";
}

function KPICard({
    label,
    value,
    change,
    up,
    icon: Icon,
    highlight = "slate",
}: KPICardProps) {
    const accent: Record<NonNullable<KPICardProps["highlight"]>, string> = {
        emerald: "bg-emerald-50 text-emerald-600",
        blue: "bg-blue-50 text-blue-600",
        violet: "bg-violet-50 text-violet-600",
        slate: "bg-slate-50 text-slate-600",
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
                <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent[highlight]}`}
                >
                    <Icon size={16} />
                </div>
                <span
                    className={`text-[10px] font-medium flex items-center gap-0.5 ${
                        up ? "text-emerald-600" : "text-amber-600"
                    }`}
                >
                    {up ? <TrendingUp size={11} /> : <AlertTriangle size={11} />}
                    {change}
                </span>
            </div>
            <p className="text-2xl font-bold text-slate-900 tracking-tight">
                {value}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Bell size={28} className="text-violet-500" />
            </div>
            <p className="text-slate-900 font-semibold mb-2">
                Todavía no enviaste notificaciones
            </p>
            <p className="text-slate-500 text-sm mb-5 max-w-md mx-auto leading-relaxed">
                Cuando envíes la primera campaña, vas a ver acá las métricas de
                entrega, apertura y clics — igual que en WhatsApp.
            </p>
            <Link to="/notifications">
                <Button variant="primary">
                    <Bell size={15} />
                    Enviar primera notificación
                </Button>
            </Link>
        </div>
    );
}

export default NotificationsAnalyticsPage;
